import { Helmet } from 'react-helmet-async';

export default function PrivacidadPage() {
  return (
    <>
      <Helmet>
        <title>Política de Privacidad y Protección de Datos | EhostingChile</title>
        <meta name="description" content="Política de privacidad y protección de datos personales de EhostingChile. Información sobre el tratamiento y seguridad de tus datos." />
        <link rel="canonical" href="https://www.ehostingchile.com/privacidad" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/5 via-teal-500/5 to-cyan-500/5"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white mb-6 shadow-lg shadow-emerald-500/30">
                <i className="ri-shield-check-line text-4xl"></i>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Política de Privacidad
              </h1>
              <p className="text-xl text-gray-600">
                Última actualización: Enero 2025
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-3xl p-10 md:p-16 shadow-xl border border-gray-100">
              
              {/* Introducción */}
              <div className="mb-12">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  En <strong>EhostingChile.com</strong> (en adelante, "EhostingChile", "nosotros" o "la Empresa"), respetamos y protegemos la privacidad de nuestros clientes y visitantes. Esta Política de Privacidad describe cómo recopilamos, usamos, almacenamos y protegemos su información personal de acuerdo con la <strong>Ley N° 19.628 sobre Protección de la Vida Privada</strong> de Chile y las mejores prácticas internacionales.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Al utilizar nuestros servicios o visitar nuestro sitio web, usted acepta las prácticas descritas en esta política. Si no está de acuerdo, le solicitamos no utilizar nuestros servicios.
                </p>
              </div>

              {/* 1. Responsable del Tratamiento */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 text-xl">1</span>
                  Responsable del Tratamiento de Datos
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    El responsable del tratamiento de sus datos personales es:
                  </p>
                  <div className="bg-gray-50 rounded-xl p-6 space-y-2">
                    <p><strong>Razón Social:</strong> EhostingChile.com</p>
                    <p><strong>Dirección:</strong> Av. Apoquindo 6410, of 202, Las Condes, Santiago, Chile</p>
                    <p><strong>Correo electrónico:</strong> privacidad@ehostingchile.com</p>
                    <p><strong>Teléfono:</strong> +56 2 2581 9100</p>
                  </div>
                  <p>
                    Para ejercer sus derechos o realizar consultas sobre el tratamiento de sus datos personales, puede contactarnos a través de los medios indicados anteriormente.
                  </p>
                </div>
              </div>

              {/* 2. Datos que Recopilamos */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 text-xl">2</span>
                  Datos Personales que Recopilamos
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Recopilamos diferentes tipos de información personal según la naturaleza de su relación con nosotros:
                  </p>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">A) Datos de Identificación y Contacto:</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Nombre completo y apellidos</li>
                        <li>RUT o número de identificación fiscal</li>
                        <li>Dirección postal completa</li>
                        <li>Correo electrónico</li>
                        <li>Número de teléfono y WhatsApp</li>
                        <li>Razón social y datos de empresa (para clientes corporativos)</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">B) Datos de Facturación y Pago:</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Información de tarjetas de crédito/débito (procesada por pasarelas de pago certificadas PCI-DSS)</li>
                        <li>Datos bancarios para transferencias</li>
                        <li>Historial de transacciones y facturas</li>
                        <li>Información tributaria para emisión de documentos fiscales</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">C) Datos Técnicos y de Uso:</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Dirección IP y ubicación geográfica aproximada</li>
                        <li>Tipo de navegador y sistema operativo</li>
                        <li>Páginas visitadas y tiempo de navegación</li>
                        <li>Cookies y tecnologías similares (ver sección de cookies)</li>
                        <li>Registros de acceso a servicios (logs de servidor, cPanel, FTP, etc.)</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">D) Datos de Soporte y Comunicaciones:</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Contenido de tickets de soporte y chats</li>
                        <li>Grabaciones de llamadas telefónicas (con previo aviso)</li>
                        <li>Correos electrónicos intercambiados</li>
                        <li>Encuestas de satisfacción y feedback</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">E) Datos Alojados en Nuestros Servidores:</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Archivos, bases de datos y contenido de sitios web</li>
                        <li>Correos electrónicos almacenados en cuentas de email</li>
                        <li>Respaldos automáticos de servicios contratados</li>
                      </ul>
                      <p className="mt-2 text-sm italic">
                        Nota: EhostingChile no accede al contenido alojado por los clientes excepto cuando sea estrictamente necesario para soporte técnico autorizado, cumplimiento legal o investigación de violaciones de términos de servicio.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Finalidad del Tratamiento */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-purple-50 text-purple-600 text-xl">3</span>
                  Finalidad del Tratamiento de Datos
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Utilizamos sus datos personales para las siguientes finalidades:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Prestación de servicios:</strong> Activar, gestionar y mantener los servicios de hosting, VPS, dominios y correo electrónico contratados.</li>
                    <li><strong>Facturación y cobros:</strong> Emitir facturas, procesar pagos, gestionar renovaciones y enviar recordatorios de vencimiento.</li>
                    <li><strong>Soporte técnico:</strong> Responder consultas, resolver problemas técnicos y proporcionar asistencia personalizada.</li>
                    <li><strong>Seguridad:</strong> Detectar y prevenir fraudes, abusos, ataques informáticos y violaciones de nuestros términos de servicio.</li>
                    <li><strong>Mejora de servicios:</strong> Analizar el uso de nuestros servicios para optimizar rendimiento, desarrollar nuevas funcionalidades y mejorar la experiencia del usuario.</li>
                    <li><strong>Comunicaciones comerciales:</strong> Enviar información sobre nuevos servicios, promociones, actualizaciones y noticias relevantes (con opción de cancelar suscripción).</li>
                    <li><strong>Cumplimiento legal:</strong> Cumplir con obligaciones legales, regulatorias y responder a requerimientos de autoridades competentes.</li>
                    <li><strong>Gestión de reclamos:</strong> Atender y resolver reclamos, disputas y solicitudes de ejercicio de derechos.</li>
                  </ul>
                </div>
              </div>

              {/* 4. Base Legal */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 text-orange-600 text-xl">4</span>
                  Base Legal del Tratamiento
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    El tratamiento de sus datos personales se fundamenta en:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Ejecución del contrato:</strong> El tratamiento es necesario para cumplir con las obligaciones contractuales derivadas de la contratación de nuestros servicios.</li>
                    <li><strong>Consentimiento:</strong> Usted ha otorgado su consentimiento expreso al aceptar nuestros términos de servicio y esta política de privacidad.</li>
                    <li><strong>Obligación legal:</strong> Debemos tratar ciertos datos para cumplir con obligaciones tributarias, contables y regulatorias en Chile.</li>
                    <li><strong>Interés legítimo:</strong> Tenemos un interés legítimo en proteger la seguridad de nuestros sistemas, prevenir fraudes y mejorar nuestros servicios.</li>
                  </ul>
                </div>
              </div>

              {/* 5. Cookies */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-600 text-xl">5</span>
                  Uso de Cookies y Tecnologías Similares
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Nuestro sitio web utiliza cookies y tecnologías similares para mejorar la experiencia del usuario y analizar el tráfico web.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Tipos de cookies que utilizamos:</h3>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Cookies esenciales:</strong> Necesarias para el funcionamiento básico del sitio (sesión de usuario, carrito de compras, seguridad).</li>
                        <li><strong>Cookies de rendimiento:</strong> Recopilan información sobre cómo los visitantes usan el sitio (Google Analytics) para mejorar su funcionamiento.</li>
                        <li><strong>Cookies funcionales:</strong> Recuerdan sus preferencias (idioma, región) para personalizar su experiencia.</li>
                        <li><strong>Cookies de marketing:</strong> Utilizadas para mostrar publicidad relevante y medir la efectividad de campañas publicitarias.</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Gestión de cookies:</h3>
                      <p>
                        Puede configurar su navegador para rechazar cookies o recibir una notificación cuando se envíen. Sin embargo, algunas funcionalidades del sitio pueden no funcionar correctamente sin cookies esenciales.
                      </p>
                      <p className="mt-2">
                        Para más información sobre cómo gestionar cookies en navegadores populares:
                      </p>
                      <ul className="list-disc pl-6 space-y-1 mt-2">
                        <li>Chrome: Configuración &gt; Privacidad y seguridad &gt; Cookies</li>
                        <li>Firefox: Opciones &gt; Privacidad y seguridad &gt; Cookies</li>
                        <li>Safari: Preferencias &gt; Privacidad &gt; Cookies</li>
                        <li>Edge: Configuración &gt; Privacidad &gt; Cookies</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. Compartir Datos */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50 text-teal-600 text-xl">6</span>
                  Compartir Datos con Terceros
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    EhostingChile no vende, alquila ni comparte sus datos personales con terceros para fines comerciales. Sin embargo, podemos compartir información limitada con:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Proveedores de servicios:</strong> Empresas que nos ayudan a operar nuestro negocio (procesadores de pago, proveedores de infraestructura, servicios de email marketing) bajo estrictos acuerdos de confidencialidad.</li>
                    <li><strong>Registros de dominios:</strong> Información necesaria para registrar dominios (nombre, dirección, email) según políticas ICANN y registros locales.</li>
                    <li><strong>Autoridades legales:</strong> Cuando sea requerido por ley, orden judicial o para proteger nuestros derechos legales.</li>
                    <li><strong>Transferencias empresariales:</strong> En caso de fusión, adquisición o venta de activos, sus datos pueden transferirse al nuevo propietario con las mismas garantías de protección.</li>
                  </ul>
                  <p className="mt-4">
                    Todos nuestros proveedores de servicios están obligados contractualmente a proteger sus datos y solo pueden usarlos para los fines específicos autorizados por nosotros.
                  </p>
                </div>
              </div>

              {/* 7. Transferencias Internacionales */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 text-xl">7</span>
                  Transferencias Internacionales de Datos
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Algunos de nuestros servidores y proveedores de servicios se encuentran fuera de Chile, incluyendo Estados Unidos y Europa. Al contratar nuestros servicios, usted consiente expresamente la transferencia internacional de sus datos personales.
                  </p>
                  <p>
                    Garantizamos que todas las transferencias internacionales cumplen con estándares adecuados de protección mediante:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Cláusulas contractuales estándar de protección de datos</li>
                    <li>Certificaciones de seguridad (ISO 27001, SOC 2)</li>
                    <li>Cumplimiento de marcos legales reconocidos (Privacy Shield, GDPR cuando aplique)</li>
                    <li>Medidas técnicas de encriptación y seguridad</li>
                  </ul>
                  <p className="mt-4">
                    Puede solicitar información adicional sobre las garantías específicas aplicadas a las transferencias internacionales contactando a privacidad@ehostingchile.com.
                  </p>
                </div>
              </div>

              {/* 8. Seguridad */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-pink-50 text-pink-600 text-xl">8</span>
                  Seguridad de los Datos
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Implementamos medidas técnicas y organizativas apropiadas para proteger sus datos personales contra acceso no autorizado, pérdida, destrucción o alteración:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Encriptación:</strong> Uso de SSL/TLS para todas las comunicaciones y almacenamiento encriptado de datos sensibles.</li>
                    <li><strong>Control de acceso:</strong> Acceso restringido a datos personales solo para personal autorizado con necesidad legítima.</li>
                    <li><strong>Autenticación:</strong> Contraseñas seguras, autenticación de dos factores y políticas de gestión de credenciales.</li>
                    <li><strong>Monitoreo:</strong> Sistemas de detección de intrusiones, análisis de vulnerabilidades y auditorías de seguridad regulares.</li>
                    <li><strong>Respaldos:</strong> Copias de seguridad automáticas y cifradas almacenadas en ubicaciones geográficamente distribuidas.</li>
                    <li><strong>Capacitación:</strong> Formación continua de nuestro personal en protección de datos y seguridad de la información.</li>
                  </ul>
                  <p className="mt-4">
                    A pesar de nuestros esfuerzos, ningún sistema es 100% seguro. En caso de una brecha de seguridad que afecte sus datos, le notificaremos de acuerdo con la legislación aplicable.
                  </p>
                </div>
              </div>

              {/* 9. Retención de Datos */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-yellow-50 text-yellow-600 text-xl">9</span>
                  Período de Retención de Datos
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Conservamos sus datos personales durante el tiempo necesario para cumplir con las finalidades descritas en esta política:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Datos de cuenta activa:</strong> Mientras mantenga servicios activos con nosotros.</li>
                    <li><strong>Datos de facturación:</strong> 6 años desde la emisión de la factura (obligación tributaria en Chile).</li>
                    <li><strong>Datos de soporte:</strong> 3 años desde la última interacción para referencia histórica.</li>
                    <li><strong>Logs de servidor:</strong> 90 días para análisis de seguridad y rendimiento.</li>
                    <li><strong>Datos de marketing:</strong> Hasta que retire su consentimiento o solicite la eliminación.</li>
                    <li><strong>Datos después de cancelación:</strong> 30 días para permitir reactivación, luego eliminación permanente (excepto datos requeridos por ley).</li>
                  </ul>
                  <p className="mt-4">
                    Después de estos períodos, sus datos serán eliminados de forma segura o anonimizados para fines estadísticos.
                  </p>
                </div>
              </div>

              {/* 10. Derechos ARCO */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-cyan-50 text-cyan-600 text-xl">10</span>
                  Sus Derechos (ARCO)
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    De acuerdo con la Ley N° 19.628 de Chile, usted tiene los siguientes derechos sobre sus datos personales:
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Derecho de Acceso:</h3>
                      <p>Solicitar información sobre qué datos personales tenemos sobre usted y cómo los utilizamos.</p>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Derecho de Rectificación:</h3>
                      <p>Solicitar la corrección de datos inexactos, incompletos o desactualizados.</p>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Derecho de Cancelación:</h3>
                      <p>Solicitar la eliminación de sus datos personales cuando ya no sean necesarios para las finalidades para las que fueron recopilados.</p>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Derecho de Oposición:</h3>
                      <p>Oponerse al tratamiento de sus datos para fines específicos, como marketing directo.</p>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Derecho a Retirar el Consentimiento:</h3>
                      <p>Retirar su consentimiento en cualquier momento cuando el tratamiento se base en consentimiento.</p>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Derecho de Portabilidad:</h3>
                      <p>Solicitar una copia de sus datos en formato estructurado y de uso común para transferirlos a otro proveedor.</p>
                    </div>
                  </div>

                  <div className="bg-emerald-50 rounded-xl p-6 mt-6">
                    <h3 className="font-bold text-gray-900 mb-3">Cómo ejercer sus derechos:</h3>
                    <p className="mb-3">Para ejercer cualquiera de estos derechos, envíe una solicitud a:</p>
                    <ul className="space-y-2">
                      <li><strong>Correo electrónico:</strong> privacidad@ehostingchile.com</li>
                      <li><strong>Correo postal:</strong> Av. Apoquindo 6410, of 202, Las Condes, Santiago, Chile</li>
                    </ul>
                    <p className="mt-3">
                      Responderemos a su solicitud dentro de los 15 días hábiles siguientes a su recepción. Podemos solicitar información adicional para verificar su identidad antes de procesar la solicitud.
                    </p>
                  </div>
                </div>
              </div>

              {/* 11. Menores de Edad */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-50 text-green-600 text-xl">11</span>
                  Protección de Menores de Edad
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Nuestros servicios están dirigidos a personas mayores de 18 años. No recopilamos intencionalmente datos personales de menores de edad sin el consentimiento de sus padres o tutores legales.
                  </p>
                  <p>
                    Si descubrimos que hemos recopilado datos de un menor sin autorización, eliminaremos esa información de inmediato. Si cree que podríamos tener información de un menor, contáctenos a privacidad@ehostingchile.com.
                  </p>
                </div>
              </div>

              {/* 12. Modificaciones */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 text-xl">12</span>
                  Modificaciones a esta Política
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios en nuestras prácticas, servicios o requisitos legales. La versión actualizada se publicará en nuestro sitio web con la fecha de "Última actualización".
                  </p>
                  <p>
                    Si realizamos cambios significativos que afecten sus derechos, le notificaremos por correo electrónico o mediante un aviso destacado en nuestro sitio web con al menos 30 días de anticipación.
                  </p>
                  <p>
                    Le recomendamos revisar esta política periódicamente para mantenerse informado sobre cómo protegemos su información.
                  </p>
                </div>
              </div>

              {/* 13. Contacto */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-purple-50 text-purple-600 text-xl">13</span>
                  Contacto y Consultas
                </h2>
                <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Si tiene preguntas, inquietudes o desea ejercer sus derechos relacionados con esta Política de Privacidad, puede contactarnos a través de:
                  </p>
                  <div className="bg-gray-50 rounded-xl p-6 space-y-3">
                    <p><strong>Oficial de Protección de Datos:</strong></p>
                    <p><strong>Correo electrónico:</strong> privacidad@ehostingchile.com</p>
                    <p><strong>Correo electrónico general:</strong> soporte@ehostingchile.com</p>
                    <p><strong>Teléfono:</strong> +56 2 2581 9100</p>
                    <p><strong>WhatsApp:</strong> +56 9 4247 0111</p>
                    <p><strong>Dirección postal:</strong> Av. Apoquindo 6410, of 202, Las Condes, Santiago, Chile</p>
                    <p><strong>Horario de atención:</strong> Lunes a Viernes 9:00 - 18:00 hrs</p>
                  </div>
                  <p className="mt-4">
                    También puede presentar una reclamación ante el Consejo para la Transparencia de Chile si considera que sus derechos de protección de datos han sido vulnerados.
                  </p>
                </div>
              </div>

              {/* Footer del documento */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-500 text-center mb-4">
                  Esta Política de Privacidad cumple con la Ley N° 19.628 sobre Protección de la Vida Privada de Chile
                </p>
                <p className="text-sm text-gray-500 text-center">
                  Última actualización: Enero 2025 · EhostingChile.com · Todos los derechos reservados
                </p>
              </div>

            </div>
          </div>
        </section>
      </div>
    </>
  );
}